import { instanceToPlain, plainToInstance } from 'class-transformer'
import { cloneDeep, isEqual } from 'lodash'
import AreaPlugin from 'rete-area-plugin'
import ConnectionPlugin from 'rete-connection-plugin'
import VueRenderPlugin from 'rete-vue-render-plugin'
import i18n from 'vue-i18n'

import { errorNotificationWrapper } from '../../../../../logic/helpers/NotificationService'
import { ChannelInfoResponse } from '../../../../../models/CheckChannels'
import { GetUsersInfo } from '../../../../../models/getUserInfo/GetUserInfo'
import { ConnectionModel } from '../../components/FlowConnection/FlowConnection'
import CustomInput from '../../components/Socket/CustomInput'
import CustomOutput from '../../components/Socket/CustomOutput'
import { ConfigDataScheme, FlowConfig } from '../../types'
import { CustomConnection } from '../Connections/CustomConnection'
import FlowHistory from "../FlowHistory";
import { FlowStatistics } from '../FlowStatistics'
import { initArea } from '../Helpers/AreaPluginBackgroundHelper'
import ChannelsInfoHelper from '../Helpers/ChannelsInfoHelper'
import UsersInfoHelper from '../Helpers/UserInfoHelper'
import { RunFlowNodeData } from '../Nodes/Additionally/RunFlow/logic/RunFlowNode'
import {
  CheckSubscriptionNodeData
} from '../Nodes/Branching/CheckSubscriptionNode/logic/CheckSubscriptionNode'
import ConstantValue
  from '../Nodes/Branching/ConditionsNode/logic/PropertyConditionValues/ConstantValue'
import { ActionNodeData } from '../Nodes/Communications/Action/logic/ActionNode'
import { KickAction } from '../Nodes/Communications/Action/logic/Actions/KickAction/KickAction'
import {
  NotifyGroupAction
} from '../Nodes/Communications/Action/logic/Actions/NotifyGroupAction/NotifyGroupAction'
import {
  NotifyUsersAction
} from '../Nodes/Communications/Action/logic/Actions/NotifyUsersAction/NotifyUsersAction'
import {
  ForwardMessageNodeData
} from '../Nodes/Communications/ForwardMessage/logic/ForwardMessageNode'
import CustomNode from '../Nodes/CustomNode'
import CustomNodeComponent from '../Nodes/CustomNodeComponent'
import { NodeTypes } from '../Nodes/NodesTypes'
import ErrorNodeBuilder from '../Nodes/SystemNodes/logic/ErrorNode'
import CustomEditorEvents from './CustomEditorEvents'
import StartNodeBuilder, { StartNode } from './StartNode/StartNode'
import { FlowSettingsController, ProjectState } from './types'
import { isChatTargetInterface } from "./utils/ChatTargetInterface";

export default abstract class CustomEditor extends CustomEditorEvents {
  abstract nodesBuilders: { [key: string]: CustomNodeComponent }
  abstract readonly: boolean
  abstract projectState: ProjectState

  errorNodeBuilder: CustomNodeComponent | null = null

  isReady = false

  telegramDataIsReady = false

  flowBackup: ConfigDataScheme | null = null

  abstract getUsersInfoRequest(users: Array<string>): Promise<GetUsersInfo>

  abstract getChannelsInfoRequest(channels: Array<string | number>): Promise<ChannelInfoResponse>

  abstract saveFlow(): Promise<void>

  getUsersInfo(users: Array<string | number>): Promise<GetUsersInfo> {
    return new Promise((resolve) => {
      const toStringData = users.map((u) => String(u))

      this.getUsersInfoRequest(toStringData)
        .then((res) => {
          const found = res.data.map((data) => data.value)
          const notFound = toStringData.filter((u) => !found.includes(u))

          UsersInfoHelper.Users.unshift(...res.data)
          UsersInfoHelper.NotFoundUsers.unshift(...notFound)

          resolve(res)
        })
    })
  }

  getChannelsInfo(channels: Array<string | number>): Promise<ChannelInfoResponse> {
    return new Promise((resolve) => {
      this.getChannelsInfoRequest(channels)
        .then((res) => {
          ChannelsInfoHelper.Channels.unshift(...res.data)
          resolve(res)
        })
    })
  }

  flowConfig: FlowConfig

  abstract flowSettingsController: FlowSettingsController

  protected constructor(container: HTMLElement, config: FlowConfig, i18n: i18n) {
    super(container, i18n)
    this.flowConfig = config
    CustomNode.i18n = i18n
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  async prepareTelegramData(): Promise<void> {
    const users: Array<number> = []
    const channels: Array<string> = []

    this.flowConfig.data.scheme.items.forEach((n) => {
      if (n.data instanceof ActionNodeData) {
        n.data.actions.forEach((a) => {
          if (a instanceof NotifyUsersAction) {
            users.push(...a.users)
          }

          if (a instanceof NotifyGroupAction && a.group) {
            channels.push(a.group.toString())
          }

          if (a instanceof KickAction) {
            channels.push(...a.targets.map(t => t.toString()))
          }

          if (isChatTargetInterface(a)) {
            channels.push(...a.getChats().map(t => t.toString()))
          }
        })
      }

      if (n.data instanceof RunFlowNodeData && n.data.target instanceof ConstantValue) {
        const targetValue = n.data.target.value

        if (typeof targetValue === 'string') {
          users.push(Number.parseInt(targetValue))
        }
      }

      if (n.data instanceof ForwardMessageNodeData) {
        channels.push(
          ...n.data.targets
            .filter(c => !!c && c < 0)
            .map((c) => c!.toString())
        )

        users.push(
          ...n.data.targets
            .filter(c => !!c && c > 0)
            .map((c) => c)
        )
      }

      if (n.data instanceof CheckSubscriptionNodeData) {
        channels.push(
          ...n.data.targets
            .filter(c => !!c)
            .map((c) => c!.toString())
        )
      }
    })

    await Promise.all([
      users.length > 0 ? this.getUsersInfo(users) : [],
      channels.length > 0 ? this.getChannelsInfo(channels) : []
    ])
    this.telegramDataIsReady = true
  }

  async createNewNode(type: string, position?: { x: number, y: number }): Promise<CustomNode | void> {
    const builder = this.nodesBuilders[type]

    if (builder) {
      const { x, y } = position ? position : this.view.area.mouse

      return await builder.createNewNode([ x, y ]).then((node) => {
        this.addNode(node)

        return node
      })
    }
  }

  getFlowConfigModel(): ConfigDataScheme {
    const nodeModels = this.nodes.map((n) => n.getModel())
    const connections: Array<ConnectionModel> = []

    for (const connectionView of this.view.connections.values()) {
      const connection = connectionView.connection as CustomConnection

      connections.push({
        input: Number(connectionView.connection.input.key),
        output: Number(connectionView.connection.output.key),
        visible: connection.data.visible,
        color: connection.data.color
      })
    }

    return plainToInstance(ConfigDataScheme, {
      index: CustomNode.LastSocketId,
      items: instanceToPlain(nodeModels),
      connections,
      accessor: this.flowConfig.data.scheme.accessor ? this.flowConfig.data.scheme.accessor.getModel() : null,
    })
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  async init(): Promise<void> {
    this.isReady = false

    this.nodesBuilders = {
      [NodeTypes.Start]: new StartNodeBuilder(this.i18n),
      ...this.nodesBuilders
    }

    Object.values(this.nodesBuilders).forEach((component) => this.register(component))
    this.errorNodeBuilder = new ErrorNodeBuilder(this.i18n)
    this.register(this.errorNodeBuilder)

    this.use(ConnectionPlugin)
    this.use(VueRenderPlugin)
    FlowHistory.init(this)

    if (!this.readonly) {
      this.use(AreaPlugin, {
        background: true,
        snap: { size: 50 },
        scaleExtent: { min: 0.1, max: 3 },
        translateExtent: false,
      })

      initArea()
    }

    CustomNode.LastSocketId = Number(this.flowConfig.data.scheme.index)

    const nodes = this.flowConfig.data.scheme.items
    const connections = this.flowConfig.data.scheme.connections

    if (!this.readonly) {
      super.setEvents()

      await this.prepareTelegramData()
    } else {
      this.on('nodetranslate', () => false)
        .on('translate', () => false)
        .on('rendersocket', ({ input, output }) => {
          if (output instanceof CustomOutput) {
            const cOutput = output

            this.sockets[cOutput.key] = cOutput
          }

          if (input instanceof CustomInput) {
            const cInput = input

            this.sockets[cInput.key] = cInput
          }
        })
    }

    for (const nodeConfig of nodes) {
      try {
        const node = await this.nodesBuilders[nodeConfig.data.type].createNode(nodeConfig)

        this.addNode(node)

        if (node instanceof StartNode) {
          FlowStatistics.StartNode = node
        }
      } catch (e) {
        this.addNode(await this.errorNodeBuilder.createNode(nodeConfig))
      }
    }

    connections.forEach((connection: ConnectionModel) => {
      const output = this.sockets[String(connection.output)]
      const input = this.sockets[String(connection.input)]

      if (output && input) {
        const { visible, color } = connection

        this.connect(output as CustomOutput, input as CustomInput, {
          visible,
          color,
          d: '',
          id: CustomConnection.LastConnectionId += 1,
        })
      }
    })

    this.eventTarget.addEventListener('saveFlow', this.saveFlow.bind(this))
    this.eventTarget.addEventListener('pasteNodes', async () => {
      for (const nodeConfig of this.nodesPlaints) {
        const node = await this.nodesBuilders[nodeConfig.data.type].createNode(nodeConfig)
        this.addNode(node)
      }
    })

    this.preZoom()

    if (this.readonly) {
      this.on('zoom', () => false)
    }

    this.flowBackup = this.getFlowConfigModel()

    this.isReady = true
  }

  isFlowChanged() {
    try {
      return !isEqual(instanceToPlain(this.flowBackup), instanceToPlain(this.getFlowConfigModel()))
    } catch (e) {
      errorNotificationWrapper(this.i18n.t('error').toString(), e)
      return false
    }
  }

  preZoom(): void {
    const nodesCoords: Array<{ x: number, y: number, width: number, height: number }> = []

    this.view.nodes.forEach((nodeView) => {
      nodesCoords.push({
        x: nodeView.node.position[0],
        y: nodeView.node.position[1],
        width: nodeView.node.position[0] + nodeView.el.clientWidth,
        height: nodeView.node.position[1] + nodeView.el.clientHeight,
      })
    })

    const nodesField = {
      top: Math.min.apply(null, nodesCoords.map((coord) => coord.y)),
      right: Math.max.apply(null, nodesCoords.map((coord) => coord.width)),
      bottom: Math.max.apply(null, nodesCoords.map((coord) => coord.height)),
      left: Math.min.apply(null, nodesCoords.map((coord) => coord.x)),
    }

    const width = nodesField.right - nodesField.left + 200
    const height = nodesField.bottom - nodesField.top + 200

    let zoomScale = 0

    if (width > height) {
      if (window.innerWidth / width < 0.1) {
        zoomScale = 0.1
      } else if (window.innerWidth / width > 3) {
        zoomScale = 3
      } else {
        zoomScale = window.innerWidth / width
      }
    } else {
      if (window.innerHeight / height < 0.1) {
        zoomScale = 0.1
      } else if (window.innerHeight / height > 3) {
        zoomScale = 3
      } else {
        zoomScale = window.innerHeight / height
      }
    }

    const heightMiddle = window.innerHeight > height * zoomScale ? (window.innerHeight - height * zoomScale) / 2 : 0
    const widthMiddle = window.innerWidth > width * zoomScale ? (window.innerWidth - width * zoomScale) / 2 : 0

    const xPosition = ((nodesField.left - 100) * zoomScale) * -1 + widthMiddle
    const yPosition = ((nodesField.top - 100) * zoomScale) * -1 + heightMiddle
    this.view.area.zoom(zoomScale, xPosition, yPosition, 'wheel')
  }
}
